// Heads-up bits shared by views: the top status bar (clock, weather, cash,
// safety) and the bottom ticker of incidents and company happenings.
import { api } from '../../convex/_generated/api';
import type { Doc } from '../../convex/_generated/dataModel';
import { COMPANY } from '../../convex/shared/scenario';
import type { Weather } from '../../convex/shared/validators';
import { dayPhase } from '../lib/daylight';
import { fmtClock, fmtDay, fmtPct, fmtRelative, fmtUsd, humanize } from '../lib/format';
import type { Cast } from '../lib/hooks';
import { useSimQuery } from '../lib/session';
import { Icon, type IconName } from '../ui/Icon';

export const WEATHER_ICON: Record<Weather, IconName> = { clear: 'sun', cloudy: 'cloud', rain: 'rain', storm: 'storm', fog: 'fog', heat: 'heat' };

export function HudBar({ now, weather, temperatureC }: { now: number; weather: Weather; temperatureC?: number }) {
  const company = useSimQuery(api.company.sim.getCompany, {});
  const phase = dayPhase(now);
  return (
    <div className="hud-bar">
      <div className="hud-chip hud-clock" title={`${COMPANY.city} local time`}>
        <Icon name={phase === 'night' ? 'moon' : 'sun'} size={18} />
        <span>
          <b>{fmtClock(now)}</b>
          <small>{fmtDay(now)}</small>
        </span>
      </div>
      <div className="hud-chip" title={`Weather: ${humanize(weather)}`}>
        <Icon name={WEATHER_ICON[weather]} size={18} />
        <span>
          <b>{temperatureC !== undefined ? `${Math.round(temperatureC)}°C` : humanize(weather)}</b>
          <small>{humanize(weather)}</small>
        </span>
      </div>
      {company ? (
        <>
          <div className="hud-chip tone-leaf" title="Cash in the bank">
            <Icon name="dollar" size={18} />
            <span>
              <b>{fmtUsd(company.cash)}</b>
              <small>cash</small>
            </span>
          </div>
          <div className="hud-chip tone-sky hide-narrow" title="Safety score (0–100)">
            <Icon name="shield" size={18} />
            <span>
              <b>{Math.round(company.kpis.safetyScore)}</b>
              <small>safety</small>
            </span>
          </div>
          <div className="hud-chip tone-sun hide-narrow" title="On-time arrivals">
            <Icon name="clock" size={18} />
            <span>
              <b>{fmtPct(company.kpis.onTimePct > 1 ? company.kpis.onTimePct / 100 : company.kpis.onTimePct)}</b>
              <small>on time</small>
            </span>
          </div>
          <div className="hud-chip tone-coral hide-narrow" title="Days since the last preventable incident">
            <Icon name="flag" size={18} />
            <span>
              <b>{company.kpis.daysSincePreventableIncident}</b>
              <small>clean days</small>
            </span>
          </div>
        </>
      ) : null}
    </div>
  );
}

type TickerItem = { id: string; at: number; text: string; tone: string; icon: IconName };

export function Ticker({ incidents, events, cast, now }: { incidents: Doc<'incidents'>[] | undefined; events: Doc<'companyEvents'>[] | undefined; cast: Cast | undefined; now: number }) {
  const items: TickerItem[] = [
    ...(incidents ?? []).slice(0, 12).map((i) => ({
      id: i._id,
      at: i.at,
      text: `${humanize(i.kind)}${i.personaId ? ` · ${cast?.nameOf(i.personaId) ?? ''}` : ''} · ${i.description}`,
      tone: i.severity === 'critical' || i.severity === 'high' ? 'coral' : i.severity === 'medium' ? 'sun' : 'sky',
      icon: (i.kind === 'collision' ? 'alert' : i.kind === 'fault_code' ? 'wrench' : i.kind === 'low_fuel' ? 'fuel' : 'gauge') as IconName,
    })),
    ...(events ?? []).slice(0, 12).map((e) => ({
      id: e._id,
      at: e.at,
      text: e.text,
      tone: e.severity === 'critical' || e.severity === 'high' ? 'coral' : 'grape',
      icon: (e.kind === 'payroll' ? 'dollar' : e.kind === 'meeting' ? 'people' : e.kind === 'weather' ? 'cloud' : 'building') as IconName,
    })),
  ]
    .sort((a, b) => b.at - a.at)
    .slice(0, 16);
  if (items.length === 0) {
    return (
      <div className="ticker is-empty">
        <span>All quiet in Austin. No incidents or company news yet.</span>
      </div>
    );
  }
  // Duplicate the run so the marquee loops seamlessly.
  const run = items.map((it) => (
    <span key={it.id} className={`ticker-item tone-${it.tone}`}>
      <Icon name={it.icon} size={14} />
      <b>{fmtRelative(it.at, now)}</b> {it.text}
    </span>
  ));
  return (
    <div className="ticker" aria-label="Latest incidents and company news">
      <div className="ticker-run" style={{ animationDuration: `${Math.max(30, items.length * 7)}s` }}>
        {run}
        <span aria-hidden className="ticker-dup">
          {run}
        </span>
      </div>
    </div>
  );
}
